import { useEffect } from "react";
import { motion } from "framer-motion";
import {
  CalendarDays,
  Clock,
  MapPin,
  IdCard,
  Phone,
  HeartHandshake,
  Leaf,
  ArrowUpRight,
} from "lucide-react";
import { FadeUp, MaskedLine } from "../components/Reveal";
import { useCountdown } from "../components/GrandOpening";

const EVENT_DATE = "2026-11-25T10:00:00";

const details = [
  {
    icon: CalendarDays,
    title: "Wednesday, November 25, 2026",
    body: "Mark your calendar — the day before Thanksgiving.",
    testid: "community-detail-date",
  },
  {
    icon: Clock,
    title: "Starting at 10:00 AM",
    body: "Until all items have been distributed.",
    testid: "community-detail-time",
  },
  {
    icon: IdCard,
    title: "Bring photo identification",
    body: "Please have your photo ID ready when you arrive.",
    testid: "community-detail-id",
  },
];

export default function Community() {
  const countdown = useCountdown(EVENT_DATE);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <section
      id="top"
      data-testid="community-page"
      className="mx-auto max-w-7xl px-6 pb-32 pt-44 sm:pt-52 lg:px-10"
    >
      <div className="max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mb-8 inline-flex items-center gap-2 rounded-full border border-[#E2DFD8] bg-white/60 px-4 py-2"
          data-testid="community-eyebrow"
        >
          <Leaf className="h-3.5 w-3.5 text-[#C85C40]" />
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5C5A]">
            Together, We Give Thanks
          </span>
        </motion.div>

        <h1 className="font-serif text-5xl font-bold leading-[0.95] tracking-tighter sm:text-7xl">
          <MaskedLine delay={0.1}>Thanksgiving</MaskedLine>
          <MaskedLine delay={0.22}>
            <span className="italic font-medium text-[#C85C40]">Turkey Basket</span>
          </MaskedLine>
          <MaskedLine delay={0.34}>Giveaway.</MaskedLine>
        </h1>

        <motion.p
          data-testid="community-partnership"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 max-w-2xl text-base leading-relaxed text-[#5C5C5A] md:text-lg"
        >
          Hosted in partnership by Spintastic City Laundry Services and{" "}
          <span className="font-semibold text-[#1A1A1A]">
            Sankofa Youth and Family Services, Inc.
          </span>{" "}
          — free Thanksgiving turkey baskets for our neighbors, right here at the
          laundromat.
        </motion.p>

        {countdown && !countdown.done && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap items-center gap-3"
            data-testid="community-countdown"
          >
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#C85C40]">
              Countdown to giveaway day
            </span>
            {[
              [countdown.days, "days"],
              [countdown.hours, "hrs"],
              [countdown.minutes, "min"],
              [countdown.seconds, "sec"],
            ].map(([v, label]) => (
              <span
                key={label}
                className="rounded-full bg-[#1A1A1A] px-4 py-2 text-sm font-bold tabular-nums text-[#F9F6F0]"
              >
                {String(v).padStart(2, "0")} {label}
              </span>
            ))}
          </motion.div>
        )}
      </div>

      <div className="mt-24 grid items-start gap-12 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-5">
          <FadeUp>
            <div
              className="overflow-hidden rounded-[2rem] shadow-[0_16px_48px_rgba(0,0,0,0.10)]"
              data-testid="community-flyer-frame"
            >
              <motion.img
                src="/assets/turkey-flyer.jpg"
                alt="Thanksgiving Turkey Basket Giveaway flyer — Wednesday, November 25, 2026 at 10:00 AM at Spintastic City Laundry Services"
                initial={{ scale: 1.1 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
                className="w-full object-cover will-change-transform"
                data-testid="community-flyer"
              />
            </div>
          </FadeUp>
        </div>

        <div className="flex flex-col gap-6 lg:col-span-7">
          {details.map((d, i) => {
            const Icon = d.icon;
            return (
              <FadeUp
                key={d.title}
                delay={i * 0.1}
                className="flex items-start gap-5 rounded-[24px] border border-[#E2DFD8]/60 bg-white/70 p-7 shadow-[0_4px_24px_rgba(0,0,0,0.04)] backdrop-blur-md"
                data-testid={d.testid}
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#C85C40]/10 text-[#C85C40]">
                  <Icon className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="font-serif text-2xl tracking-tight">{d.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-[#5C5C5A]">
                    {d.body}
                  </p>
                </div>
              </FadeUp>
            );
          })}

          <FadeUp
            delay={0.3}
            className="flex items-start gap-5 rounded-[24px] border border-[#E2DFD8]/60 bg-white/70 p-7 shadow-[0_4px_24px_rgba(0,0,0,0.04)] backdrop-blur-md"
            data-testid="community-detail-location"
          >
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#7D8C74]/15 text-[#7D8C74]">
              <MapPin className="h-5 w-5" />
            </span>
            <div>
              <h3 className="font-serif text-2xl tracking-tight">
                Spintastic City Laundry Services
              </h3>
              <p className="mt-1.5 text-sm leading-relaxed text-[#5C5C5A]">
                6980 Indian Head Highway, Bryans Road, MD 20616
              </p>
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=6980+Indian+Head+Highway+Bryans+Road+MD+20616"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="community-directions-link"
                className="group mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-[#C85C40] transition-colors duration-300 hover:text-[#A84C34]"
              >
                Get directions
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>
          </FadeUp>

          <FadeUp
            delay={0.4}
            className="flex items-start gap-5 rounded-[24px] border border-[#E2DFD8]/60 bg-white/70 p-7 shadow-[0_4px_24px_rgba(0,0,0,0.04)] backdrop-blur-md"
            data-testid="community-detail-contact"
          >
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#C85C40]/10 text-[#C85C40]">
              <Phone className="h-5 w-5" />
            </span>
            <div>
              <h3 className="font-serif text-2xl tracking-tight">Questions?</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-[#5C5C5A]">
                Call{" "}
                <a
                  href="tel:+12403837489"
                  data-testid="community-contact-alex"
                  className="font-bold text-[#1A1A1A] transition-colors duration-300 hover:text-[#C85C40]"
                >
                  Alex: (240) 383-7489
                </a>{" "}
                or{" "}
                <a
                  href="tel:+12403942516"
                  data-testid="community-contact-tawana"
                  className="font-bold text-[#1A1A1A] transition-colors duration-300 hover:text-[#C85C40]"
                >
                  Tawana: (240) 394-2516
                </a>
              </p>
            </div>
          </FadeUp>
        </div>
      </div>

      <FadeUp delay={0.15} className="mt-24">
        <div
          className="flex flex-col items-center gap-4 rounded-[2rem] bg-[#1A1A1A] px-8 py-14 text-center text-[#F9F6F0]"
          data-testid="community-tagline-band"
        >
          <HeartHandshake className="h-8 w-8 text-[#C85C40]" />
          <p className="font-serif text-3xl italic tracking-tight sm:text-4xl">
            Sharing gratitude. Supporting families. Serving our community.
          </p>
        </div>
      </FadeUp>
    </section>
  );
}
